import { ChangeEvent } from 'react';
import clsx from 'clsx';
import { TemplateMeta } from '../../lib/api';

interface Props {
  filters: TemplateMeta['filters'];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">{children}</label>;
}

const inputClass = 'w-full px-2.5 py-1.5 text-sm bg-surface border border-border rounded-md focus:outline-none focus:border-accent';

export function FilterPanel({ filters, values, onChange }: Props) {
  const set = (key: string, value: any) => onChange({ ...values, [key]: value });

  return (
    <div className="space-y-4">
      {filters.map(f => {
        if (f.type === 'dateRange') {
          return (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={values.dateFrom ?? ''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ ...values, dateFrom: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="date"
                  value={values.dateTo ?? ''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ ...values, dateTo: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          );
        }
        if (f.type === 'text') {
          return (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              <input
                type="text"
                value={values[f.key] ?? ''}
                onChange={e => set(f.key, e.target.value || null)}
                className={inputClass}
              />
            </div>
          );
        }
        if (f.type === 'number') {
          return (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              <input
                type="number"
                step="0.01"
                value={values[f.key] ?? (f.defaultValue as number | undefined) ?? ''}
                onChange={e => set(f.key, e.target.value === '' ? null : Number(e.target.value))}
                className={inputClass}
              />
            </div>
          );
        }
        if (f.type === 'singleSelect') {
          return (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              <select
                value={values[f.key] ?? f.defaultValue ?? 'all'}
                onChange={e => set(f.key, e.target.value === 'all' ? null : e.target.value)}
                className={inputClass}
              >
                {f.options?.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          );
        }
        if (f.type === 'multiSelect') {
          const selected: string[] = values[f.key] ?? [];
          return (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              <input
                type="text"
                placeholder="Comma-separated IDs"
                value={Array.isArray(selected) ? selected.join(',') : ''}
                onChange={e => set(f.key, e.target.value ? e.target.value.split(',').map(s => s.trim()) : null)}
                className={inputClass}
              />
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
